<script setup lang="ts">
  import { getInputAriaDescribedBy } from "@/lib/inputComponentUtils"
  import { useBreakpoint } from "@/lib/useBreakpoint"
  import { cn } from "@/lib/utils"
  import { SupportingLabel } from "@/types/InputComponentTypes"
  import { useVModel } from "@vueuse/core"
  import { useId } from "radix-vue"
  import { computed } from "vue"
  import { Button } from "@/components/ui/button"
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
  } from "@/components/ui/form"
  import FormSupportingLabel from "@/components/ui/form/FormSupportingLabel.vue"
  import Icon from "@/components/ui/icons/Icon.vue"
  import {
    PinInput,
    PinInputGroup,
    PinInputInput,
    PinInputSeparator,
  } from "@/components/ui/pin-input"

  export type MeterReadInputProps = {
    modelValue?: string[]
    inputLength: number
    label: string
    name?: string
    labelIcon?: "Night" | "Day"
    showRedDigit?: boolean
    invalid?: boolean
    disabled?: boolean
    required?: boolean
    supportingLabels?: SupportingLabel[]
    keepSupportingLabelsOnError?: boolean
    hasClearBtn?: boolean
  }

  const isTablet = useBreakpoint("sm")
  const isDesktop = useBreakpoint("md")

  const props = withDefaults(defineProps<MeterReadInputProps>(), {
    modelValue: () => [],
    name: "meterRead",
    showRedDigit: false,
    invalid: false,
    disabled: false,
    required: false,
    hasClearBtn: false,
  })

  const emits = defineEmits<{
    "update:modelValue": [value: string[]]
    clearModel: []
  }>()
  const modelValue = useVModel(props, "modelValue", emits, {
    passive: true,
    defaultValue: [],
  })

  const getAriaDescribedBy = (formMessageId: string, error: string) =>
    getInputAriaDescribedBy(
      {
        supportingLabels: props.supportingLabels,
        error,
      },
      formMessageId,
    )

  const meterReadLabelId = useId(undefined, "meter-read-label")

  const clearModel = () => {
    modelValue.value = []
    emits("clearModel")
  }

  const isRedDigitVisible = computed(() => {
    if (!props.showRedDigit) return false

    if (isDesktop.value) return true

    if (isTablet.value) return props.inputLength < 7

    return props.inputLength < 8
  })
</script>

<template>
  <FormField
    v-slot="{ field }"
    v-model="modelValue"
    :name="name"
    :validate-on-blur="false"
    :validate-on-change="false"
    :validate-on-model-update="false"
  >
    <FormItem v-bind="$attrs">
      <FormControl v-slot="{ error, formItemId, ariaInvalid, formMessageId }">
        <FormLabel v-if="label" :id="meterReadLabelId" data-testid="form-label">
          <Icon
            v-if="labelIcon"
            :name="labelIcon"
            size="lg"
            class="mr-2 inline-block align-bottom"
          />{{ label }}
        </FormLabel>

        <PinInput
          :id="formItemId"
          :name="field.name"
          :aria-invalid="ariaInvalid"
          :model-value="field.value"
          :aria-describedby="getAriaDescribedBy(formMessageId, error)"
          placeholder="0"
          type="number"
          class="max-w-[348px]"
          data-testid="meter-read-input"
          :disabled="disabled"
          :required="required"
          @update:model-value="(e) => emits('update:modelValue', e)"
        >
          <PinInputGroup>
            <PinInputInput
              v-for="index in inputLength"
              :key="index"
              :index="index - 1"
              :aria-describedby="`${meterReadLabelId} ${getAriaDescribedBy(formMessageId, error)}`"
              :aria-label="`Enter digit ${index}`"
              :class="
                cn(
                  (invalid || error) &&
                    '!border-destructive !ring-0 transition-none',
                )
              "
            />
            <div v-if="isRedDigitVisible" class="flex items-center">
              <PinInputSeparator class="px-1" />

              <PinInputInput
                :index="-1"
                class="w-[38px] min-w-[38px] disabled:border-destructive disabled:bg-transparent disabled:placeholder-destructive"
                disabled
                placeholder="X"
              />
            </div>
          </PinInputGroup>
        </PinInput>

        <template
          v-if="
            supportingLabels?.length && (keepSupportingLabelsOnError || !error)
          "
        >
          <FormSupportingLabel
            v-for="(supportingLabel, index) in supportingLabels"
            :id="`${formMessageId}-${index}`"
            :key="`${formMessageId}-${index}`"
            :supporting-label="supportingLabel"
            :data-testid="`form-supporting-label-${index}`"
          >
          </FormSupportingLabel>
        </template>
        <FormMessage />
        <Button
          v-if="hasClearBtn"
          data-testid="clear-button"
          variant="outline"
          size="sm"
          class="ml-auto block"
          @click.prevent="clearModel"
          >Clear</Button
        >
      </FormControl>
    </FormItem>
  </FormField>
</template>
